import { DateTime } from '@ksyos/date-time';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IRequest } from '@frontend/components/interfaces';
import { AbortProcessReasons, EpisodeCategory, RequestStatus, ServiceCode } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { fetchJson } from '@frontend/store/fetch';
import { removeUrlParameter } from '@frontend/utils';
import { translateKey } from '../../../../../translate';

export function OsasqConfirmationModal(props: { patientId: string; serviceId: string }) {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [question, setQuestion] = useState<string>('');
    const [shouldAbortPreviousOsasq, setShouldAbortPreviousOsasq] = useState<boolean>(false);
    const [osasqDateCompleted, setOsasqDateCompleted] = useState<string>();
    const [latestNonOsasqRequestId, setLatestNonCompletedOsasqRequestId] = useState<string>();
    const [isLatestOsasqStartedButNotCompleted, setIsLatestOsasqStartedButNotCompleted] = useState<boolean>(false);

    useEffect(() => {
        loadPreviousOsasqRequests();
    }, []);

    return (
        <ConfirmationModal
            cancelText={translateKey('common.cancel')}
            confirmText={translateKey('request.resendQuestionnaires')}
            handleNo={() => {
                setShowModal(false);
                navigate(-1);
            }}
            handleYes={async () => {
                if (shouldAbortPreviousOsasq) {
                    await abortPreviousOsasqRequest();
                    setShowModal(false);

                    let url = removeUrlParameter(window.location.search, 'requestId');
                    if (url.indexOf('serviceId') < 0) {
                        const urlBuilder = new UrlBuilder(url, { isFirstParameter: false });
                        urlBuilder.add('serviceId', props.serviceId);
                        url = urlBuilder.build();
                    }
                    window.location.replace(url);
                } else if (osasqDateCompleted || isLatestOsasqStartedButNotCompleted) {
                    setShowModal(false);
                } else {
                    navigate(-1);
                }
            }}
            hideConfirmButton={!(shouldAbortPreviousOsasq || osasqDateCompleted || isLatestOsasqStartedButNotCompleted)}
            isOpen={showModal}
            question={question}
            title={translateKey('request.resendQuestionnairesModalTitle')}
        />
    );

    async function loadPreviousOsasqRequests() {
        const { latestNonCompletedOsasqRequest, mostRecentlyCompletedOsasqRequest } = await getPreviousOsasq();
        let latestNonCompletedOsasqProcedure;

        if (latestNonCompletedOsasqRequest?.id) {
            latestNonCompletedOsasqProcedure = (
                await fetchJson(
                    `/api/requests/${latestNonCompletedOsasqRequest.id}/procedure`,
                    'GET',
                    undefined,
                    [],
                    [400, 404]
                )
            ).json;

            setLatestNonCompletedOsasqRequestId(latestNonCompletedOsasqRequest?.id);
        }

        let shouldShowModal = false;

        const latestNonCompletededOsasqDate =
            latestNonCompletedOsasqRequest?.customControlData?.resend_date || latestNonCompletedOsasqRequest?.dateSent;

        if (latestNonCompletededOsasqDate) {
            let shouldAbortPreviousOsasqRequest = true;
            const osasqProcedureQuestionnaires =
                latestNonCompletedOsasqProcedure?.customControlData?.questionnaires ?? [];
            const formattedQuestionnairesDate = DateTime.from(latestNonCompletededOsasqDate).format('DD-MM-YYYY');

            if (DateTime.now().isSameOrBefore(latestNonCompletededOsasqDate)) {
                setQuestion(translateKey('request.alreadyScheduledQuestionnaires', formattedQuestionnairesDate));
            } else if (!osasqProcedureQuestionnaires.length) {
                setQuestion(translateKey('request.questionnairesNotStarted', formattedQuestionnairesDate));
            } else {
                setQuestion(translateKey('request.questionnairesStartedButNotFinished', formattedQuestionnairesDate));
                shouldAbortPreviousOsasqRequest = false;
                setIsLatestOsasqStartedButNotCompleted(true);
            }

            shouldShowModal = true;
            setShouldAbortPreviousOsasq(shouldAbortPreviousOsasqRequest);
        } else if (mostRecentlyCompletedOsasqRequest?.closedDateTime) {
            setOsasqDateCompleted(mostRecentlyCompletedOsasqRequest.closedDateTime);
            setQuestion(
                translateKey(
                    'request.questionnairesStartedAndFinished',
                    DateTime.from(mostRecentlyCompletedOsasqRequest.closedDateTime).format('DD-MM-YYYY')
                )
            );
            shouldShowModal = true;
        }

        setShowModal(shouldShowModal);
    }

    async function abortPreviousOsasqRequest() {
        const url = `/api/requests/${latestNonOsasqRequestId}/abort`;
        await fetchJson(url, 'POST', {
            reasonStopProccess: AbortProcessReasons.newQuestionnairesSent,
        });
    }

    async function getPreviousOsasq() {
        const previousRequests = (
            await fetchJson(
                `/api/requestProcesses?patientId=${props.patientId}&episodeCategory=${EpisodeCategory.somnology}&orderByCreatedAtDesc=true`,
                'GET',
                undefined,
                [],
                [400]
            )
        ).json.items;

        let latestNonCompletedOsasqRequest: IRequest | undefined;
        let mostRecentlyCompletedOsasqRequest: IRequest | undefined;

        if (!previousRequests.errorCode) {
            latestNonCompletedOsasqRequest = getLatestNonCompletedOsasRequestForServiceCode(
                previousRequests,
                ServiceCode.OSASQ
            );
            mostRecentlyCompletedOsasqRequest = getLatestCompletedOsasRequestInThePrevious28Days(
                previousRequests,
                ServiceCode.OSASQ
            );

            if (latestNonCompletedOsasqRequest?.id) {
                latestNonCompletedOsasqRequest.customControlData = (
                    await fetchJson(`/api/requests/${latestNonCompletedOsasqRequest.id}/customControlData`, 'GET')
                ).json?.customControlData;
            }

            if (mostRecentlyCompletedOsasqRequest?.id) {
                const recentlyCompletedOsasqRequestCustomControlData = await fetchJson(
                    `/api/requests/${mostRecentlyCompletedOsasqRequest.id}/customControlData`,
                    'GET'
                );
                mostRecentlyCompletedOsasqRequest.customControlData =
                    recentlyCompletedOsasqRequestCustomControlData.json?.customControlData;
            }
        }

        return {
            latestNonCompletedOsasqRequest,
            mostRecentlyCompletedOsasqRequest,
        };
    }

    function getLatestNonCompletedOsasRequestForServiceCode(previousRequests: any, code: ServiceCode) {
        return previousRequests.find(
            (item: { code: ServiceCode; status: RequestStatus }) =>
                item.code === code && item.status !== RequestStatus.completed
        );
    }

    function getLatestCompletedOsasRequestInThePrevious28Days(previousRequests: any, code: ServiceCode) {
        return previousRequests.find(
            (item: { closedDateTime: Date; code: ServiceCode; status: RequestStatus }) =>
                item.code === code &&
                item.status === RequestStatus.completed &&
                DateTime.from(item.closedDateTime).add(28, 'days').isAfter(DateTime.now())
        );
    }
}
